import { Activity, RefObject, useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import TableData from '@layouts/layout-table/layout-table-data';
import TableRow from '@layouts/layout-table/layout-table-row';
import Chip from '@styles/design-system/components/chips/chip';
import BrandliveAvatar from '@ui/brandlive-avatar/brandlive-avatar';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import { Speaker } from '@/types/speakers.types';
import getCastInviteStatus from './helpers/use-cast-select-invite-status';
import GlobalContainer from '@ui/container/global-container';
import CastActions from './cast-actions';
import useGetSpeakerRole from '../../helpers/use-get-speaker-role';
import { selectGreenroomSessionMeetings, selectSessionByUuid } from '@/store/slices/project/selectors';
import TextClippedTooltip from '@ui/text/text/text-clipped-tooltip';
// see implementation comment below
// import toggleGreenroomWatcherStatus from './helpers/toggle-greenroom-watcher-status';

type Props = {
  speaker: Speaker;
  sessionUuid: string;
  setEditSpeaker: (speaker: Speaker | null) => void;
  scrollRef?: RefObject<HTMLDivElement | null>;
  inert?: boolean;
};

const CastContentTableRow = ({ inert = false, speaker, sessionUuid, setEditSpeaker, scrollRef }: Props) => {
  const activeChannel = useAppSelector(getActiveChannel);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const meetings = useAppSelector(selectGreenroomSessionMeetings);
  const sessionMeetingData = meetings?.[sessionUuid || ''] || null;

  const session = useAppSelector(selectSessionByUuid(sessionUuid));

  const role = useGetSpeakerRole({ speaker, sessionUuid });

  const status = useMemo(
    () => getCastInviteStatus({ speaker, activeChannel, sessionMeetingData }),
    [activeChannel, sessionMeetingData, speaker]
  );

  // see implementation comment below
  // const speakerBlAdminId = speaker.bl_admin?.id;

  if (!activeChannel || !session) return null;
  return (
    <TableRow
      backgroundColor={popoverIsOpen ? 'var(--global-cont-1)' : 'transparent'}
      hoverBackgroundColor="var(--global-cont-1)"
      focusWithinBackgroundColor="var(--global-cont-1)"
      border={0}
      inert={inert}
      className="cursor-pointer"
      // Entire row is clickable to open edit speaker details modal
      // we don't need this to be accessible by keyboard as there is a separate edit button in the actions
      onClick={() => {
        setEditSpeaker(speaker);
      }}
    >
      <TableData borderBottom="1px solid var(--global-cont-2)" width="200px">
        <div className="flex ai-center gap-8 margin-l-8">
          <div className="position-relative">
            <BrandliveAvatar profile={speaker} borderRadius="8px" />
            <Activity mode={status === 'Invite sent' ? 'visible' : 'hidden'}>
              <GlobalContainer
                className="position-absolute radius-full flex ai-center pc-center square-14"
                css={{
                  top: 'calc(100% - 8px)',
                  left: 'calc(100% - 8px)',
                  background: 'var(--primitives-mode-1-orange-500)',
                  border: '1px solid var(--BG)',
                }}
              >
                <Icon name={ICONS.SEND_3} size={10} color="var(--local-bttn-sec-default-icotext)" />
              </GlobalContainer>
            </Activity>
          </div>
          <Text className="P2" clamp={1}>
            {`${speaker.first_name} ${speaker.last_name}`.trim()}
          </Text>
        </div>
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="260px">
        <TextClippedTooltip
          textProps={{ className: 'P2', wordBreak: 'break-all' }}
          tooltipPortalProps={{ keepOpenOnContentHover: false, tailSize: 0 }}
          tooltipText={speaker.email || ''}
          text={speaker.email || ''}
        />
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="240px">
        <div className="flex">
          <Chip
            label={role}
            size={32}
            noHover
            /* Commenting out this for now. It's not part of mvp. However, the logic is correct and works. */
            // {...(speakerBlAdminId && status !== ''
            //   ? {
            //       onClick: () => {
            //         const greenroomInvite = sessionMeetingData?.invites?.find(
            //           (p) => p.profile?.events_profile === speakerBlAdminId
            //         );
            //         const isWatcher = !!greenroomInvite?.watcher_only;
            //         toggleGreenroomWatcherStatus({
            //           sessionMeetingData,
            //           isWatcher: !isWatcher,
            //           speakerBlAdminId,
            //           session,
            //           dispatch,
            //         });
            //       },
            //     }
            //   : {
            //       noHover: true,
            //     })}
          />
        </div>
      </TableData>
      <TableData borderBottom="1px solid var(--global-cont-2)" width="264px">
        <Text className="P2">{status}</Text>
      </TableData>

      <TableData borderBottom="1px solid var(--global-cont-2)" width="48px" padding={0}>
        <div className="flex jc-flex-end margin-r-8">
          <CastActions
            speaker={speaker}
            containerRef={scrollRef}
            sessionUuid={sessionUuid}
            onMenuOpen={() => setPopoverIsOpen(true)}
            onMenuClose={() => setPopoverIsOpen(false)}
            onEditSpeakerClick={setEditSpeaker}
          />
        </div>
      </TableData>
    </TableRow>
  );
};

export default CastContentTableRow;
